'use client';

import React from 'react';
import Cookies from 'js-cookie';

interface GeolocationResponse {
  country: string;
  ip: string | null;
  source: string;
  isGDPR: boolean;
}

const useCookieConsent = () => {
  const [consent, setConsent] = React.useState<boolean | null>(null);
  const [userRegion, setUserRegion] = React.useState<string>('');
  const [isGDPRRegion, setIsGDPRRegion] = React.useState<boolean>(false);
  const [showBanner, setShowBanner] = React.useState<boolean>(false);
  const [hasUserInteracted, setHasUserInteracted] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    // Detect user's region using our production geolocation API
    const detectUserRegion = async () => {
      try {
        const response = await fetch('/api/geolocation');

        if (response.ok) {
          const data: GeolocationResponse = await response.json();
          setUserRegion(data.country);
          setIsGDPRRegion(data.isGDPR);

          // Check if user has previously interacted with consent
          const existingConsent = Cookies.get('localConsent');
          const hasInteracted = existingConsent !== undefined;

          if (hasInteracted) {
            // User has previously made a choice - respect it
            setConsent(existingConsent === 'true');
            setHasUserInteracted(true);
            setShowBanner(false);
          } else {
            // First visit - set region-specific defaults
            if (data.isGDPR) {
              // GDPR region - deny by default, require explicit consent
              setConsent(false);
              setShowBanner(true); // Always show for GDPR
            } else {
              // Non-GDPR region - grant by default, allow opt-out
              setConsent(true);
              setShowBanner(true); // Show for transparency and opt-out option
            }
            setHasUserInteracted(false);
          }
        } else {
          throw new Error('Geolocation API failed');
        }
      } catch (error) {
        console.warn(
          'Could not detect user region, defaulting to US behavior:',
          error,
        );

        // Fallback to non-GDPR behavior if detection fails
        setUserRegion('US');
        setIsGDPRRegion(false);

        const existingConsent = Cookies.get('localConsent');
        const hasInteracted = existingConsent !== undefined;

        if (hasInteracted) {
          setConsent(existingConsent === 'true');
          setHasUserInteracted(true);
          setShowBanner(false);
        } else {
          setConsent(true); // Default to granted for non-GDPR
          setHasUserInteracted(false);
          setShowBanner(true);
        }
      }
    };

    detectUserRegion();
  }, []);

  const acceptCookie = () => {
    setConsent(true);
    setShowBanner(false);
    setHasUserInteracted(true);
    Cookies.set('localConsent', 'true', { expires: 365, sameSite: 'lax' });

    // Update Google Consent Mode v2 when user accepts
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      });
    }
  };

  const rejectCookie = () => {
    setConsent(false);
    setShowBanner(false);
    setHasUserInteracted(true);
    Cookies.set('localConsent', 'false', { expires: 365, sameSite: 'lax' });

    // Ensure consent remains denied
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
      });
    }
  };

  return {
    consent,
    showBanner,
    userRegion,
    isGDPRRegion,
    hasUserInteracted,
    acceptCookie,
    rejectCookie,
  };
};

export const CookieConsent = () => {
  const { showBanner, isGDPRRegion, acceptCookie, rejectCookie } =
    useCookieConsent();

  if (!showBanner) {
    return null;
  }

  return (
    <div className="relative inset-0 z-10">
      <div className="fixed bottom-0 left-0 right-0 flex items-center justify-center bg-menu-gradient px-4 py-2 animate-slide-up">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full max-w-6xl">
          <span className="text-white text-sm mb-2 sm:mb-0 sm:mr-4">
            {isGDPRRegion ? (
              // GDPR region messaging - explicit consent required
              <>
                We use cookies and similar technologies to enhance your
                experience, analyze site usage, and assist with marketing
                efforts. By clicking &quot;Accept&quot;, you consent to our use
                of cookies.{' '}
                <a
                  href="/privacy-policy"
                  className="underline hover:no-underline"
                >
                  Privacy Policy
                </a>
              </>
            ) : (
              // Non-GDPR region messaging - opt-out model
              <>
                We use cookies to ensure that we give you the best experience on
                our website. You can opt out at any time.{' '}
                <a
                  href="/privacy-policy"
                  className="underline hover:no-underline"
                >
                  Privacy Policy
                </a>
              </>
            )}
          </span>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              className="py-2 px-4 button-bordered text-sm"
              onClick={acceptCookie}
              aria-label="Accept cookies"
            >
              {isGDPRRegion ? 'Accept' : 'Continue'}
            </button>
            {/* Only show reject/opt-out button in GDPR regions */}
            {isGDPRRegion && (
              <button
                className="py-2 px-4 bg-transparent border border-white text-white hover:bg-white hover:text-black transition-colors text-sm"
                onClick={rejectCookie}
                aria-label="Reject cookies"
              >
                Reject
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
